
// src/router/index.js

import {
  createRouter,
  createWebHistory,
} from "vue-router";

import {
  auth,
  authReady,
} from "@/firebase";

import NotFound from "@/views/NotFound.vue";
import Login from "@/views/Login.vue";
import Signup from "@/views/Signup.vue";
import VenueDashboard from "@/views/VenueDashboard.vue";
import TaskView from "@/views/TaskView.vue";

/* FINANCE */
import TillsView from "@/views/TillsView.vue";
import ComparisonsView from "@/views/ComparisonsView.vue";
import PettyCashView from "@/views/PettyCashView.vue";
import ForecastView from "@/views/ForecastView.vue";
import SafeCountView from "@/views/SafeCountView.vue";
import UtilitiesView from "@/views/UtilitiesView.vue";
import LiquorOrdering from "@/views/LiquorOrdering.vue";

/* STOCK */
import StockView from "@/views/StockView.vue";
import DeliveryNotesView from "@/views/DeliveryNotesView.vue";
import StockReportsView from "@/views/StockReportsView.vue";

/* LABOUR */
import RotaView from "@/views/RotaView.vue";
import WageSummaryView from "@/views/WageSummaryView.vue";
import HolidayRequestView from "@/views/HolidayRequestView.vue";

/* DUE DILIGENCE */
import VenueAuditView from "@/views/VenueAuditView.vue";
import IncidentReportView from "@/views/IncidentReportFormView.vue";
import DocLibrary from "@/views/DocLibrary.vue";
import CoreInformation from "@/views/CoreInformation.vue";
import TrainingView from "@/views/TrainingView.vue";

/* MARKETING */
import ArtworkWorkflowTracker from "@/views/ArtworkWorkflowTracker.vue";
import MailerView from "@/views/MailerView.vue";

/* MANAGER */
import ManagerAddStaff from "@/views/manager/ManagerAddStaff.vue";

/* EMAIL LINK */
import EmailLinkFinishView from "@/components/rota/EmailLinkFinishView.vue";

/* AUTHENTICATION */
import {
  assertUserIsAllowed,
} from "@/scripts/authGate";

import {
  resolveAccessMode,
  setCachedMode,
  getCachedMode,
} from "@/scripts/authMode";

/* =========================================================
   LAST VENUE
   ========================================================= */

function getLastVenueId() {
  try {
    return (
      localStorage.getItem("coredeck_lastVenueId") ||
      null
    );
  } catch {
    return null;
  }
}

/* =========================================================
   USER ROLE DETECTION
   ========================================================= */

function isSocialMediaUser(profile = {}, claims = {}) {
  return (
    claims.social_media === true ||
    claims.socialMedia === true ||
    claims.is_social_media === true ||
    profile?.isSocialMedia === true ||
    profile?.is_social_media === true ||
    profile?.social_media === true ||
    String(
      profile?.user_type ||
      profile?.userType ||
      ""
    )
      .trim()
      .toLowerCase() === "social_media"
  );
}

function isOperationsUser(profile = {}, claims = {}) {
  return (
    claims.operations === true ||
    claims.ops === true ||
    claims.is_operations === true ||
    profile?.isOperations === true ||
    profile?.is_operations === true ||
    profile?.operations === true ||
    String(
      profile?.user_type ||
      profile?.userType ||
      ""
    )
      .trim()
      .toLowerCase() === "operations"
  );
}

function isSocialMediaAllowedRoute(to) {
  return (
    to.name === "ArtworkWorkflow" ||
    to.name === "ArtworkWorkflowTracker" ||
    to.name === "Mailer" ||
    String(to.path || "")
      .toLowerCase()
      .includes("artwork") ||
    String(to.path || "")
      .toLowerCase()
      .includes("mailer")
  );
}

/* =========================================================
   PROFILE VENUE
   ========================================================= */

function getProfileVenueId(profile = {}) {
  return (
    getLastVenueId() ||
    profile?.venueId ||
    profile?.venue_id ||
    profile?.defaultVenueId ||
    profile?.default_venue_id ||
    (
      Array.isArray(profile?.venueIds)
        ? profile.venueIds[0]
        : null
    ) ||
    (
      Array.isArray(profile?.venue_ids)
        ? profile.venue_ids[0]
        : null
    ) ||
    null
  );
}

/* =========================================================
   SOCIAL MEDIA REDIRECT
   ========================================================= */

function socialMediaArtworkRedirect(profile = {}, to = {}) {
  const venueId =
    getProfileVenueId(profile) ||
    to.params?.venueId ||
    null;

  if (venueId) {
    return {
      name: "ArtworkWorkflow",
      params: { venueId },
    };
  }

  return {
    name: "ArtworkWorkflow",
  };
}

/* =========================================================
   STAFF APP
   ========================================================= */

function staffAppBaseUrl() {
  const origin = window.location.origin;

  const isLocal =
    origin.includes("localhost") ||
    origin.includes("127.0.0.1");

  if (isLocal) {
    return "http://localhost:8081/";
  }

  return `${origin}/staff/`;
}

function redirectToStaffApp(pathAfterStaff = "") {
  const base = staffAppBaseUrl();

  const safePath = String(
    pathAfterStaff || ""
  ).replace(/^\/+/, "");

  window.location.replace(base + safePath);
}

/* =========================================================
   AUTH MODE
   ========================================================= */

async function getMode(user) {
  const cached = getCachedMode();

  if (cached) {
    return cached;
  }

  const { mode } = await resolveAccessMode(user);

  setCachedMode(mode);

  return mode;
}

/* =========================================================
   ROUTES
   ========================================================= */

const routes = [
  /* STAFF APP */
  {
    path: "/staff/:pathMatch(.*)*",
    name: "StaffAppProxy",

    beforeEnter(to) {
      const bits = to.params?.pathMatch;

      const tail = Array.isArray(bits)
        ? bits.join("/")
        : String(bits || "");

      redirectToStaffApp(tail);
    },

    meta: {
      public: true,
      hideNav: true,
    },
  },

  /* ROOT */
  {
    path: "/",
    name: "Root",

    async beforeEnter(to, from, next) {
      await authReady;

      const user = auth.currentUser;

      if (!user) {
        return next({
          name: "Login",
        });
      }

      const mode = await getMode(user);

      if (mode === "rota_view_only") {
        return next({
          name: "StaffAppProxy",
        });
      }

      try {
        const profile = await assertUserIsAllowed(user);

        const token = await user.getIdTokenResult(true);

        const claims = token?.claims || {};

        if (isSocialMediaUser(profile, claims)) {
          return next(
            socialMediaArtworkRedirect(profile, to)
          );
        }

        const lastVenueId =
          getLastVenueId() ||
          profile?.venueId ||
          profile?.defaultVenueId ||
          profile?.default_venue_id ||
          null;

        if (lastVenueId) {
          return next({
            name: "VenueDashboard",
            params: {
              venueId: lastVenueId,
            },
          });
        }

        return next({
          name: "VenueDashboard",
        });
      } catch {
        return next({
          name: "Login",
          query: {
            reason: "unauthorised",
          },
        });
      }
    },

    meta: {
      public: true,
      hideNav: true,
    },
  },

  /* LOGIN */
  {
    path: "/login",
    name: "Login",
    component: Login,

    meta: {
      hideNav: true,
      public: true,
    },
  },

  /* SIGNUP */
  {
    path: "/signup",
    name: "Signup",
    component: Signup,

    meta: {
      hideNav: true,
      public: true,
    },
  },

  /* FINISH SIGN IN */
  {
    path: "/finish-sign-in",
    name: "FinishSignIn",
    component: EmailLinkFinishView,

    meta: {
      hideNav: true,
      public: true,
    },
  },

  /* DASHBOARD */
  {
    path: "/:venueId?/venue_dashboard",
    name: "VenueDashboard",
    component: VenueDashboard,

    props: (route) => ({
      venueId: route.params.venueId ?? null,
      venue: route.params.venueId ?? null,
    }),
  },

  /* TASKS — OPERATIONS ONLY */
  {
    path: "/:venueId?/tasks",
    name: "Tasks",
    component: TaskView,

    meta: {
      requiresAuth: true,
      operationsOnly: true,
    },

    props: true,
  },

  /* FINANCE */
  {
    path: "/:venueId?/tills",
    name: "Tills",
    component: TillsView,
    props: true,
  },

  {
    path: "/:venueId?/comparisons",
    name: "Comparisons",
    component: ComparisonsView,
    props: true,
  },

  {
    path: "/:venueId?/petty-cash",
    name: "PettyCash",
    component: PettyCashView,
    props: true,
  },

  {
    path: "/:venueId?/forecast",
    name: "Forecast",
    component: ForecastView,
    props: true,
  },

  {
    path: "/:venueId?/safe_count",
    name: "SafeCount",
    component: SafeCountView,
    props: true,
  },

  {
    path: "/:venueId?/utilities",
    name: "Utilities",
    component: UtilitiesView,
    props: true,
  },

  {
    path: "/:venueId?/ordering",
    name: "LiquorOrdering",
    component: LiquorOrdering,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  /* BUDGET — OPERATIONS ONLY */
  {
    path: "/:venueId?/finance/budget",
    name: "Budget",
    component: () => import("@/views/BudgetView.vue"),

    meta: {
      requiresAuth: true,
      operationsOnly: true,
    },

    props: true,
  },

  /* STOCK */
  {
    path: "/:venueId?/stock",
    name: "Stock",
    component: StockView,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  {
    path: "/:venueId?/stock/delivery-notes",
    name: "DeliveryNotes",
    component: DeliveryNotesView,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  {
    path: "/:venueId?/stock/reports",
    name: "StockReports",
    component: StockReportsView,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  /* LABOUR */
  {
    path: "/:venueId?/rota",
    name: "Rota",
    component: RotaView,
    props: true,
  },

  {
    path: "/:venueId?/wage-summary",
    name: "WageSummary",
    component: WageSummaryView,
    props: true,
  },

  {
    path: "/:venueId?/holiday-requests",
    name: "HolidayRequests",
    component: HolidayRequestView,
    props: true,
  },

  /* DUE DILIGENCE */
  {
    path: "/:venueId?/venue-audit",
    name: "VenueAudit",
    component: VenueAuditView,
    props: true,
  },

  {
    path: "/:venueId?/doc-library",
    name: "DocLibrary",
    component: DocLibrary,
    props: true,
  },

  {
    path: "/:venueId?/incidents_report",
    name: "IncidentReports",
    component: IncidentReportView,
    props: true,
  },

  {
    path: "/:venueId?/opening-checks",
    name: "OpeningChecks",
    component: () => import("@/views/OpeningChecks.vue"),
    props: true,
  },

  {
    path: "/:venueId?/fire-safety",
    name: "FireSafety",
    component: () => import("@/views/FireSafetyView.vue"),

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  /* TRAINING — OPERATIONS / MULTI-SITE / SINGLE-SITE */
  {
    path: "/:venueId?/training",
    name: "TrainingView",
    component: TrainingView,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  {
    path: "/:venueId?/core-information",
    name: "CoreInformation",
    component: CoreInformation,
    props: true,
  },

  /* EVENT ROI */
  {
    path: "/:venueId?/event-roi",
    name: "EventRoi",
    component: () => import("@/views/EventRoi.vue"),

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  /* ARTWORK */
  {
    path: "/:venueId?/artwork-workflow",
    name: "ArtworkWorkflow",
    component: ArtworkWorkflowTracker,

    meta: {
      requiresAuth: true,
    },

    props: true,
  },

  /* MAILER */
  {
    path: "/:venueId?/mailer",
    name: "Mailer",
    component: MailerView,

    meta: {
      requiresAuth: true,
      mailerOnly: true,
    },

    props: true,
  },

  /* USER ADMIN */
  {
    path: "/:venueId?/admin/users",
    name: "AdminUsers",
    component: () => import("@/views/admin/AdminUserView.vue"),

    meta: {
      requiresAdmin: true,
    },

    props: true,
  },

  {
    path: "/:venueId?/admin/venues",
    name: "AdminVenue",
    component: () => import("@/views/admin/AdminVenueView.vue"),

    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },

    props: true,
  },

  /* MANAGER ADD STAFF */
  {
    path: "/:venueId?/manager/add-staff",
    name: "ManagerAddStaff",
    component: ManagerAddStaff,
    props: true,
  },

  /* SIGN-IN ALIAS */
  {
    path: "/finishSignIn",

    redirect: () => ({
      name: "FinishSignIn",
    }),
  },

  /* NOT FOUND */
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
  },
];

/* =========================================================
   ROUTER
   ========================================================= */

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/* =========================================================
   AUTHENTICATION / ACCESS GUARD
   ========================================================= */

router.beforeEach(async (to, from, next) => {
  if (!to.matched.length) {
    return next({
      name: "NotFound",
    });
  }

  await authReady;

  const user = auth.currentUser;

  const isPublic = Boolean(to.meta?.public);

  if (to.name === "StaffAppProxy") {
    return next();
  }

  if (isPublic) {
    if (
      user &&
      (
        to.name === "Login" ||
        to.name === "Signup"
      )
    ) {
      const mode = await getMode(user);

      if (mode === "rota_view_only") {
        return next({
          name: "StaffAppProxy",
        });
      }
    }

    return next();
  }

  if (!user) {
    return next({
      name: "Login",
    });
  }

  const mode = await getMode(user);

  if (mode === "rota_view_only") {
    return next({
      name: "StaffAppProxy",
    });
  }

  try {
    const userProfile = await assertUserIsAllowed(user);

    const token = await user.getIdTokenResult(true);

    const claims = token?.claims || {};

    const socialMediaUser = isSocialMediaUser(
      userProfile,
      claims
    );

    const operationsUser = isOperationsUser(
      userProfile,
      claims
    );

    /* SOCIAL MEDIA RESTRICTIONS */
    if (
      socialMediaUser &&
      !isSocialMediaAllowedRoute(to)
    ) {
      return next(
        socialMediaArtworkRedirect(userProfile, to)
      );
    }

    /* MAILER RESTRICTIONS */
    if (
      to.meta?.mailerOnly &&
      !operationsUser &&
      !socialMediaUser
    ) {
      return next({
        name: "VenueDashboard",
        params: {
          venueId: to.params?.venueId,
        },
      });
    }

    /* ADMIN RESTRICTIONS */
    if (to.meta?.requiresAdmin) {
      const userRole = userProfile?.role ?? null;

      if (userRole !== "admin") {
        return next({
          name: "VenueDashboard",
          params: {
            venueId: to.params?.venueId,
          },
        });
      }
    }

    /* OPERATIONS-ONLY ROUTES */
    if (
      to.meta?.operationsOnly &&
      !operationsUser
    ) {
      return next({
        name: "VenueDashboard",
        params: {
          venueId: to.params?.venueId,
        },
      });
    }

    /*
     * TrainingView is no longer Operations-only.
     *
     * Operations: all-venue combined dashboard.
     * Multi-site: selected-venue training report.
     * Single-site: own-venue training report.
     *
     * TrainingView.vue selects the display.
     */
  } catch (error) {
    console.error(
      "[Router] Access check failed:",
      error
    );

    return next({
      name: "Login",
      query: {
        reason: "unauthorised",
      },
    });
  }

  next();
});

router.onError((error) => {
  console.error(
    "Router error:",
    error
  );
});

export default router;
